import { DatePicker } from "antd";
import { Switch } from "V2.0/common/switch";

import dayjs from "dayjs";
import { Button } from "V2.0/common/button";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMemo, useRef, useState } from "react";
import clsx from "clsx";
import SlotTag from "./SlotTag";

const WEEKDAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const MAX_SLOT_TAG_LENGTH = 24;

export function getAvailableSlotTags(scheduleData) {
  const slotTags = new Set();

  Object.values(scheduleData).map(
    (daySchedule: {
      checked: boolean;
      slots: Array<{ startTime: string; endTime: string; slotTag?: string }>;
    }) => {
      daySchedule.slots.forEach((slot) => {
        if (slot?.slotTag) {
          slotTags.add(slot.slotTag);
        }
      });
    }
  );

  return Array.from(slotTags);
}

function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function formatTime12h(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
}

// What shows under each day name in the left list — the actual hours,
// not just a count, so the whole week is scannable without clicking in.
function getDaySummary(daySchedule) {
  if (!daySchedule?.checked) return "Closed";
  const slots = daySchedule.slots || [];
  if (slots.length === 0) return "Closed";
  if (slots.length === 1) {
    const { startTime, endTime, slotTag } = slots[0];
    const timeLabel = `${formatTime12h(startTime)} – ${formatTime12h(endTime)}`;
    return slotTag ? `${timeLabel} · ${slotTag}` : timeLabel;
  }
  return `${slots.length} time ranges`;
}

function Day(props) {
  const { day, summary, isActive, onDayClick, onToggle, checked, locked } = props;
  return (
    <div
      className={clsx(
        "flex gap-x-4 items-center cursor-pointer pl-3 pr-4 min-h-10 py-1.5 rounded-lg",
        isActive
          ? "bg-gray-100 border-emerald-500"
          : "border-transparent hover:outline hover:outline-zinc-200"
      )}
      onClick={onDayClick}
    >
      <div className="shrink-0 self-start mt-0.5">
        <Switch
          color="emerald"
          checked={checked}
          onClick={() => {
            onToggle(!checked);
          }}
          disabled={locked}
        />
      </div>

      <div className="flex flex-col leading-tight">
        <p
          className={clsx(
            "text-sm",
            isActive ? "text-gray-900 font-semibold" : "text-gray-400"
          )}
        >
          {capitalizeFirstLetter(day)}
        </p>
        <p className={clsx("text-xs", checked ? "text-gray-400" : "text-gray-300")}>
          {summary}
        </p>
      </div>
    </div>
  );
}

function Time(props) {
  const { time, autoFocus, onChange, locked } = props;
  return (
    <DatePicker
      autoFocus={autoFocus}
      mode="time"
      picker="time"
      format="HH:mm"
      onChange={onChange}
      value={dayjs(time, "HH:mm")}
      allowClear={false}
      disabled={locked}
      needConfirm={false}
      changeOnScroll
    />
  );
}

function TimeSlots(props) {
  const { data, setData, showSlotTags, locked } = props;
  const [activeDay, setActiveDay] = useState("monday");
  const [copyFeedback, setCopyFeedback] = useState(false);
  const endRef = useRef(null);

  const updateDateChecked = (dateChecked) => {
    const { day, checked } = dateChecked;
    setData((prevData) => {
      return {
        ...prevData,
        [day]: {
          ...prevData[day],
          checked,
        },
      };
    });
  };

  const addSlot = (day) => {
    setData((prevData) => {
      return {
        ...prevData,
        [day]: {
          ...prevData[day],
          slots: [
            ...prevData[day].slots,
            { startTime: "10:00", endTime: "16:00", slotTag: "" },
          ],
        },
      };
    });
    setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const removeSlot = (day, idx) => {
    setData((prevData) => {
      return {
        ...prevData,
        [day]: {
          ...prevData[day],
          slots: prevData[day].slots.toSpliced(idx, 1),
        },
      };
    });
  };

  const updateSlot = (idx, slot) => {
    setData((prevData) => {
      return {
        ...prevData,
        [activeDay]: {
          ...prevData[activeDay],
          slots: [
            ...prevData[activeDay].slots.slice(0, idx),
            slot,
            ...prevData[activeDay].slots.slice(idx + 1),
          ],
        },
      };
    });
  };

  // Copies the active day's slots onto every other day and turns them all
  // on — the main fix for "set the same hours 7 times."
  const copyToAllDays = (sourceDay) => {
    if (locked) return;
    setData((prevData) => {
      const sourceSlots = prevData[sourceDay].slots.map((s) => ({ ...s }));
      const next = { ...prevData };
      Object.keys(next).forEach((day) => {
        next[day] = { ...next[day], checked: true, slots: sourceSlots.map((s) => ({ ...s })) };
      });
      return next;
    });
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 1600);
  };

  const applyPreset = (preset) => {
    if (locked) return;
    setData((prevData) => {
      const next = { ...prevData };
      Object.keys(next).forEach((day) => {
        if (preset === "everyday") {
          next[day] = { checked: true, slots: [{ startTime: "09:00", endTime: "17:00", slotTag: "" }] };
        } else if (preset === "weekdays") {
          next[day] = WEEKDAY_KEYS.includes(day)
            ? { checked: true, slots: [{ startTime: "09:00", endTime: "17:00", slotTag: "" }] }
            : { ...next[day], checked: false };
        } else if (preset === "24-7") {
          next[day] = { checked: true, slots: [{ startTime: "00:00", endTime: "23:59", slotTag: "" }] };
        } else if (preset === "clear") {
          next[day] = { ...next[day], checked: false };
        }
      });
      return next;
    });
  };

  const availableSlotTags = useMemo(() => {
    return getAvailableSlotTags(data);
  }, [data]);

  const activeDayChecked = !!data[activeDay]?.checked;

  return (
    <div className="flex flex-col h-full min-h-0">
      {!locked && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b">
          <span className="text-xs font-medium text-gray-500 mr-1">Quick set</span>
          <button
            type="button"
            onClick={() => applyPreset("everyday")}
            className="text-xs px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300 transition-colors"
          >
            Every day, 9–5
          </button>
          <button
            type="button"
            onClick={() => applyPreset("weekdays")}
            className="text-xs px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300 transition-colors"
          >
            Weekdays only
          </button>
          <button
            type="button"
            onClick={() => applyPreset("24-7")}
            className="text-xs px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300 transition-colors"
          >
            Open 24/7
          </button>
          <button
            type="button"
            onClick={() => applyPreset("clear")}
            className="text-xs px-2.5 py-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors ml-auto"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="flex rounded-lg flex-1 min-h-0">
        <div className="w-1/3 flex flex-col gap-y-2 p-2 h-full min-h-0 overflow-scroll border-r border-gray-100">
          {Object.keys(data).map((day) => {
            const checked = data[day]?.checked;
            return (
              <Day
                key={day}
                summary={getDaySummary(data[day])}
                isActive={day === activeDay}
                day={day}
                checked={checked}
                onToggle={(state) => {
                  updateDateChecked({
                    day,
                    checked: state,
                  });
                }}
                onDayClick={() => {
                  setActiveDay(day);
                }}
                locked={locked}
              />
            );
          })}
        </div>
        <div className="flex-1 flex flex-col gap-y-2 p-2 h-full min-h-0">
          <div className="flex flex-col gap-y-3 px-4 h-full">
            <div className="flex justify-between items-center pb-3">
              <p className="font-medium text-base">
                {capitalizeFirstLetter(activeDay)}
              </p>
              {!locked && (
                <div className="flex items-center gap-2">
                  <Button
                    outline
                    disabled={!activeDayChecked}
                    onClick={() => copyToAllDays(activeDay)}
                  >
                    {copyFeedback ? "Copied ✓" : "Copy to all days"}
                  </Button>
                  <Button outline onClick={() => addSlot(activeDay)}>
                    <PlusIcon /> Add slot
                  </Button>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-y-2 overflow-auto">
              {!activeDayChecked && (
                <p className="text-sm text-gray-400 px-2 py-1">
                  {capitalizeFirstLetter(activeDay)} is closed. Toggle it on from the list to add hours.
                </p>
              )}
              {activeDayChecked &&
                data[activeDay]?.slots.map((slot, idx) => {
                  const { startTime, endTime } = slot;
                  return (
                    <div
                      className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 group hover:bg-zinc-50 p-2 rounded-lg"
                      key={`${slot}_${idx}`}
                    >
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
                        {data[activeDay]?.slots.length > 1 && (
                          <p className="text-sm font-medium text-gray-400">
                            {idx + 1}.
                          </p>
                        )}
                        <Time
                          time={startTime}
                          locked={locked}
                          onChange={(date, dateString) => {
                            // needConfirm={false} can fire onChange on blur with
                            // no real selection (date/dateString empty) — ignore
                            // those so an accidental click-away doesn't wipe the time.
                            if (!date || !dateString) return;
                            updateSlot(idx, {
                              startTime: dateString,
                              endTime: dateString > endTime ? dateString : endTime,
                              ...(slot?.slotTag ? { slotTag: slot?.slotTag } : {}),
                            });
                          }}
                        />
                        <p className="text-base font-light text-gray-400">-</p>
                        <Time
                          time={endTime}
                          locked={locked}
                          onChange={(date, dateString) => {
                            if (!date || !dateString) return;
                            updateSlot(idx, {
                              startTime,
                              endTime: dateString,
                              ...(slot?.slotTag ? { slotTag: slot?.slotTag } : {}),
                            });
                          }}
                        />
                        {showSlotTags && !locked && (
                          <div className="min-w-0 max-w-full break-words">
                            <SlotTag
                              slot={slot}
                              onSlotTagUpdate={(updatedSlot) => {
                                const slotTag =
                                  typeof updatedSlot?.slotTag === "string"
                                    ? updatedSlot.slotTag.slice(0, MAX_SLOT_TAG_LENGTH)
                                    : updatedSlot?.slotTag;
                                updateSlot(idx, { ...updatedSlot, slotTag });
                              }}
                              availableSlotTags={availableSlotTags}
                            />
                          </div>
                        )}
                      </div>

                      {!locked && (
                        <div>
                          <XMarkIcon
                            onClick={() => removeSlot(activeDay, idx)}
                            className={clsx(
                              data[activeDay]?.slots.length <= 1
                                ? "invisible"
                                : "invisible group-hover:visible hover:text-red-500 size-4"
                            )}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              <div ref={endRef} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { TimeSlots };